import { ProcurementReferences } from './purchase-order.service'
import { RequisitionStatus, RequisitionOutcome } from '../constants/requisition'
import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { environment } from '../../environments/environment'

export type RequisitionItem = Partial<ProcurementReferences> & {
  productId: string
  productName: string
  brand?: string
  sellerCompanyId: string
  supplierName: string
  requestedQuantity: number
  unitOfMeasure: string
  priceAmount: number | null
  priceCurrency: string | null
  itemId: string | null
  supplierId: string | null
  batchId: string | null
  outcome: RequisitionOutcome | null
  approvedQuantity: number | null
  rejectionReason: string | null
}

export type Requisition = {
  id: string
  reference: string
  companyId: string
  requesterId: string
  approverId: string | null
  status: RequisitionStatus
  revision: number
  recipient: string
  deliveryAddress: string
  collectionLocation: string
  note: string
  neededBy: string | null
  businessUnitId: string | null
  storageLocation: string | null
  companyCode: string | null
  items: RequisitionItem[]
  submittedAt: string | null
  reviewedBy: string | null
  reviewedAt: string | null
}

export type RequisitionDecision = {
  productId: string
  outcome: RequisitionOutcome | ''
  approvedQuantity: number | null
  rejectionReason: string
}

export type RequisitionApprover = { id: string; email: string }

@Injectable({ providedIn: 'root' })
export class RequisitionService {
  private http = inject(HttpClient)
  private url = environment.baseUrl + 'requisitions'

  list(): Observable<Requisition[]> {
    return this.http.get<Requisition[]>(this.url)
  }
  get(id: string): Observable<Requisition> {
    return this.http.get<Requisition>(`${this.url}/${encodeURIComponent(id)}`)
  }
  downloadApproved(id: string): Observable<Blob> {
    return this.http.get(`${this.url}/${encodeURIComponent(id)}/approved`, {
      responseType: 'blob',
    })
  }
  approvers(): Observable<RequisitionApprover[]> {
    return this.http.get<RequisitionApprover[]>(`${this.url}/approvers`)
  }
  createFromCart(id: string, revision: number): Observable<Requisition> {
    return this.http.put<Requisition>(
      `${this.url}/${encodeURIComponent(id)}/from-cart`,
      { revision },
    )
  }
  save(
    request: Requisition,
    acceptedUnitChanges: Record<string, string> = {},
  ): Observable<Requisition> {
    return this.http.put<Requisition>(
      `${this.url}/${encodeURIComponent(request.id)}`,
      { ...request, acceptedUnitChanges },
    )
  }
  review(
    id: string,
    revision: number,
    decisions: RequisitionDecision[],
  ): Observable<Requisition> {
    return this.http.post<Requisition>(
      `${this.url}/${encodeURIComponent(id)}/review`,
      { revision, decisions },
    )
  }
  submit(id: string, revision: number): Observable<Requisition> {
    return this.http.post<Requisition>(
      `${this.url}/${encodeURIComponent(id)}/submit`,
      { revision },
    )
  }
}
